import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  ChevronUp, 
  MoreVertical, 
  Settings, 
  Ban, 
  Clock,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';

interface Activity {
  id: string;
  user_id: string;
  user_name: string;
  date: string;
  type: 'assessment' | 'login' | 'profile_update';
  description: string;
  status: 'completed' | 'in_progress' | 'error';
}

interface SortConfig {
  key: keyof Activity;
  direction: 'asc' | 'desc';
}

const ITEMS_PER_PAGE = 25;

const getStatusColor = (status: Activity['status']) => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'in_progress':
      return 'bg-orange-100 text-orange-800';
    case 'error':
      return 'bg-red-100 text-red-800';
  }
};

const getActivityTypeLabel = (type: Activity['type']) => {
  switch (type) {
    case 'assessment':
      return 'Diagnostic';
    case 'login':
      return 'Connexion';
    case 'profile_update':
      return 'Mise à jour profil';
  }
};

export function AdminActivities() {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'date', direction: 'desc' });
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  const loadActivities = useCallback(async () => {
    try {
      setLoading(true);
      
      // Optimisation de la requête pour inclure plus d'informations
      const { data: assessments, error: assessmentsError } = await supabase
        .from('assessments')
        .select(`
          id,
          user_id,
          date,
          zone,
          overall_energy,
          symptoms,
          response,
          profiles (
            id,
            email,
            display_name,
            energy_type,
            created_at
          )
        `)
        .order('date', { ascending: false });

      if (assessmentsError) throw assessmentsError;

      // Amélioration du formatage des activités
      const formattedActivities: Activity[] = (assessments || []).map(assessment => ({
        id: assessment.id,
        user_id: assessment.user_id,
        user_name: assessment.profiles?.display_name || assessment.profiles?.email || 'Utilisateur inconnu',
        date: assessment.date,
        type: 'assessment',
        description: `Diagnostic énergétique - Niveau: ${assessment.overall_energy}% (${assessment.zone})
          ${assessment.symptoms?.length ? `\nSymptômes: ${assessment.symptoms.join(', ')}` : ''}
          ${assessment.response ? `\nRéponse: ${assessment.response}` : ''}`,
        status: 'completed'
      }));

      setActivities(formattedActivities);
    } catch (error) {
      console.error('Error loading activities:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les activités.",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadActivities();

    // Souscription aux mises à jour en temps réel
    const subscription = supabase
      .channel('activities-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'assessments' },
        () => loadActivities()
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [loadActivities]);

  // Filtrage
  const filteredActivities = activities.filter(activity => {
    const searchString = searchTerm.toLowerCase();
    return (
      activity.user_name.toLowerCase().includes(searchString) ||
      activity.description.toLowerCase().includes(searchString) ||
      getActivityTypeLabel(activity.type).toLowerCase().includes(searchString)
    );
  });

  // Tri
  const sortedActivities = [...filteredActivities].sort((a, b) => {
    const { key, direction } = sortConfig;
    const modifier = direction === 'asc' ? 1 : -1;
    
    if (a[key] < b[key]) return -1 * modifier;
    if (a[key] > b[key]) return 1 * modifier;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedActivities.length / ITEMS_PER_PAGE);
  const paginatedActivities = sortedActivities.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleSort = (key: keyof Activity) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const toggleDescription = (id: string) => {
    setExpandedDescriptions(current => {
      const newSet = new Set(current);
      if (current.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#54075B]"></div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-lg font-semibold">Activités des utilisateurs</h3>
            <p className="text-sm text-gray-500">
              Suivi des actions des utilisateurs sur la plateforme
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => loadActivities()}
              className="gap-2"
            >
              <Filter className="w-4 h-4" />
              Filtres
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th 
                  className="text-left py-3 px-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('user_name')}
                >
                  <div className="flex items-center gap-2">
                    Utilisateur
                    {sortConfig.key === 'user_name' && (
                      sortConfig.direction === 'asc' ? 
                        <ChevronUp className="w-4 h-4" /> : 
                        <ChevronDown className="w-4 h-4" />
                    )}
                  </div>
                </th>
                <th 
                  className="text-left py-3 px-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center gap-2">
                    Date/Heure
                    {sortConfig.key === 'date' && (
                      sortConfig.direction === 'asc' ? 
                        <ChevronUp className="w-4 h-4" /> : 
                        <ChevronDown className="w-4 h-4" />
                    )}
                  </div>
                </th>
                <th 
                  className="text-left py-3 px-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('type')}
                >
                  <div className="flex items-center gap-2">
                    Type
                    {sortConfig.key === 'type' && (
                      sortConfig.direction === 'asc' ? 
                        <ChevronUp className="w-4 h-4" /> : 
                        <ChevronDown className="w-4 h-4" />
                    )}
                  </div>
                </th>
                <th className="text-left py-3 px-4">Description</th>
                <th className="text-left py-3 px-4">Statut</th>
              </tr>
            </thead>
            <tbody>
              {paginatedActivities.map((activity) => (
                <tr key={activity.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4">{activity.user_name}</td>
                  <td className="py-3 px-4">
                    {format(new Date(activity.date), "dd/MM/yyyy HH:mm", { locale: fr })}
                  </td>
                  <td className="py-3 px-4">{getActivityTypeLabel(activity.type)}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <span className={expandedDescriptions.has(activity.id) ? '' : 'line-clamp-1'}>
                        {activity.description}
                      </span>
                      {activity.description.length > 100 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleDescription(activity.id)}
                          className="p-1 h-auto"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-sm ${getStatusColor(activity.status)}`}>
                      {activity.status === 'completed' ? 'Complété' :
                       activity.status === 'in_progress' ? 'En cours' : 'Erreur'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Affichage de {((currentPage - 1) * ITEMS_PER_PAGE) + 1} à{' '}
            {Math.min(currentPage * ITEMS_PER_PAGE, filteredActivities.length)} sur{' '}
            {filteredActivities.length} entrées
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Précédent
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <Button
                  key={page}
                  variant={currentPage === page ? 'default' : 'outline'}
                  onClick={() => setCurrentPage(page)}
                  className="w-8 h-8 p-0"
                >
                  {page}
                </Button>
              ))}
            </div>
            <Button
              variant="outline"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Suivant
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}